import {
  Button,
  Center,
  Checkbox,
  CloseButton,
  Dialog,
  Field,
  Fieldset,
  HStack,
  Input,
  InputGroup,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { formatGraphQlError, validationErrors } from '@client/module/error/error';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { currencySymbol, formatRawCurrency, normalizeCurrency } from '@packages/core/utils/currency-formatter';
import AlertError from '@packages/ui/alert/alert-error';
import { useTranslate } from '@tolgee/react';
import { toaster } from 'provider/src/module/chakra-ui/components/ui/toaster.tsx';
import { useEffect, useState, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  DaysOfWeek,
  HappyHourDeliveryType,
  HappyHourDiscountType,
  MenuSectionTimeRuleType,
} from '../../../../graphql/types/graphql';
import SelectDaysOfWeek from '../../menu/menu-section-create/component/select-days-of-week';
import useDeleteHappyHour from '../logics/use-delete-happy-hour';
import useGetSingleHappyHour from '../logics/use-get-single-happy-hour';
import useUpdateHappyHour from '../logics/use-update-happy-hout';
import DeliveryTypeMultipleSelection from './delivery-selection';
import DiscountTypeSelection from './discount-type-selection';

interface FormValues {
  deliveryType: HappyHourDeliveryType[];
  discountAmount: string;
  discountType: HappyHourDiscountType;
  minimumOrderAmount: string;
  startTime: string;
  endTime: string;
  isAllDays: boolean;
  daysOfWeek: DaysOfWeek[];
}

export default function HappyHourUpdateModal(props: { id: string }) {
  const { t } = useTranslate();
  const branch = useFocusedBranchContext();
  const company = useFocusedCompanyContext();
  const { data, loading } = useGetSingleHappyHour(props.id);
  const [update, { loading: updateLoading, error: updateError, reset: updateReset }] = useUpdateHappyHour();
  const [deleteHappyHour, { loading: deleteLoading }] = useDeleteHappyHour();
  const [open, setOpen] = useState(false);

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      deliveryType: [],
      discountAmount: undefined,
      discountType: HappyHourDiscountType.Amount,
      minimumOrderAmount: undefined,
      startTime: '01:00',
      endTime: '23:59',
      daysOfWeek: [],
      isAllDays: false,
    },
  });

  useEffect(() => {
    if (data?.getHappyHour) {
      reset({
        deliveryType: data.getHappyHour.deliveryType,
        discountAmount:
          data.getHappyHour.discountType == HappyHourDiscountType.Amount
            ? formatRawCurrency(data.getHappyHour.discountAmount, company.settings.currency).value
            : data.getHappyHour.discountAmount.toString(),
        discountType: data.getHappyHour.discountType,
        minimumOrderAmount: formatRawCurrency(data.getHappyHour.minimumOrderAmount, company.settings.currency).value,
        startTime:
          data.getHappyHour.timeRule.__typename === 'MenuSectionRangeTimeRule'
            ? data.getHappyHour.timeRule.start
            : '01:00',
        endTime:
          data.getHappyHour.timeRule.__typename === 'MenuSectionRangeTimeRule'
            ? data.getHappyHour.timeRule.end
            : '23:59',
        isAllDays:
          data.getHappyHour.timeRule.__typename === 'MenuSectionRangeTimeRule'
            ? (data.getHappyHour.timeRule.isAllDays ?? false)
            : true,
        daysOfWeek:
          data.getHappyHour.timeRule.__typename === 'MenuSectionRangeTimeRule'
            ? (data.getHappyHour.timeRule.daysOfWeek ?? [])
            : [],
      });
      updateReset();
    }
  }, [company.settings.currency, data?.getHappyHour, reset, updateReset, open]);

  const onSubmit = () => {
    update({
      variables: {
        id: props.id,
        input: {
          branchIds: [branch.branch?.id ?? ''],
          deliveryType: watch('deliveryType'),
          discountAmount:
            watch('discountType') == HappyHourDiscountType.Amount
              ? Number(normalizeCurrency(watch('discountAmount').toString(), company.settings.currency))
              : Number(watch('discountAmount')),
          discountType: watch('discountType'),
          minimumOrderAmount: Number(
            normalizeCurrency(watch('minimumOrderAmount').toString(), company.settings.currency),
          ),
        },
        timeRuleInput: {
          isAllDays: watch('isAllDays'),
          type: MenuSectionTimeRuleType.RangeTime,
          start: watch('startTime'),
          end: watch('endTime'),
          daysOfWeek: watch('daysOfWeek'),
        },
      },
    })
      .then(() => {
        toaster.create({
          description: t('happy-hour.update.alert', 'Update happy hour successful'),
          type: 'info',
        });
        setOpen(false);
      })
      .catch(() => {
        toaster.create({
          description: t('happy-hour.update.alert.failed', 'Update happy hour failed'),
          type: 'error',
        });
      });
  };

  const handleDelete = () => {
    deleteHappyHour({
      variables: {
        id: props.id,
      },
    })
      .then(() => {
        toaster.create({
          description: t('happy-hour.delete.alert', 'Delete happy hour successful'),
          type: 'info',
        });
        setOpen(false);
      })
      .catch(() => {
        toaster.create({
          description: t('happy-hour.delete.alert.failed', 'Delete happy hour failed'),
          type: 'error',
        });
      });
  };

  const handleCurrency = useCallback(() => {
    const discountType = watch('discountType');
    if (discountType == HappyHourDiscountType.Percentage) {
      return '%';
    }

    return currencySymbol(company.settings.currency);
  }, [watch, company.settings.currency]);

  if (loading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  const applicationErrors = formatGraphQlError(updateError?.graphQLErrors);
  const validationError = validationErrors(applicationErrors);

  const FieldSet = (
    <Fieldset.Root size="lg" maxW="md">
      <Fieldset.Content>
        <Field.Root>
          <Field.Label>{t('happy-hour.create.delivery-type', 'Delivery Type')}</Field.Label>
          <Controller
            control={control}
            name="deliveryType"
            rules={{
              required: t('happy-hour.create.delivery-type-required', 'Delivery type is required'),
              validate: (value) =>
                value.length > 0 || t('happy-hour.create.delivery-type-required', 'Delivery type is required'),
            }}
            render={({ field }) => (
              <DeliveryTypeMultipleSelection
                deliveryType={field.value}
                onChange={(deliveryType: HappyHourDeliveryType[]) => {
                  field.onChange(deliveryType);
                }}
              />
            )}
          />
          {errors.deliveryType && (
            <Text color="red.500" fontSize="sm">
              {errors.deliveryType.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('happy-hour.create.discount-type', 'Discount Type')}</Field.Label>
          <DiscountTypeSelection
            discountType={watch('discountType')}
            onChange={(value: HappyHourDiscountType) => {
              setValue('discountType', value);
            }}
          />
          {errors.discountType && (
            <Text color="red.500" fontSize="sm">
              {errors.discountType.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('happy-hour.create.discount-amount', 'Discount Amount')}</Field.Label>
          <InputGroup endElement={handleCurrency()}>
            <Input
              type="number"
              borderColor={'gray.300'}
              rounded="sm"
              {...register('discountAmount', {
                required: t('happy-hour.create.discount-amount-required', 'Discount amount is required'),
                min: {
                  value: 0,
                  message: t('happy-hour.create.discount-amount-min', 'Discount amount must be greater than 0'),
                },
                max: {
                  value: 100,
                  message: t('happy-hour.create.discount-amount-max', 'Discount amount must be less than 100'),
                },
              })}
            />
          </InputGroup>
          {errors.discountAmount && (
            <Text color="red.500" fontSize="sm">
              {errors.discountAmount.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('happy-hour.create.minimum-order-amount', 'Minimum Order Amount')}</Field.Label>
          <InputGroup endElement={currencySymbol(company.settings.currency)}>
            <Input
              type="number"
              borderColor={'gray.300'}
              rounded="sm"
              {...register('minimumOrderAmount', {
                required: t('happy-hour.create.minimum-order-amount-required', 'Minimum order amount is required'),
                min: {
                  value: 0,
                  message: t(
                    'happy-hour.create.minimum-order-amount-min',
                    'Minimum order amount must be greater than 0',
                  ),
                },
              })}
            />
          </InputGroup>
          {errors.minimumOrderAmount && (
            <Text color="red.500" fontSize="sm">
              {errors.minimumOrderAmount.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('happy-hour.create.is-all-days', 'All day')}</Field.Label>
          <Controller
            control={control}
            name="isAllDays"
            render={({ field }) => (
              <Checkbox.Root
                checked={field.value}
                variant={'subtle'}
                onCheckedChange={({ checked }) => {
                  field.onChange(checked);
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label />
              </Checkbox.Root>
            )}
          />
          {errors.isAllDays && (
            <Text color="red.500" fontSize="sm">
              {errors.isAllDays.message}
            </Text>
          )}
        </Field.Root>

        {!watch('isAllDays') && (
          <>
            <Field.Root>
              <Field.Label>{t('happy-hour.create.start-time', 'Start Time')}</Field.Label>
              <Input
                type="time"
                borderColor={'gray.300'}
                rounded="sm"
                {...register('startTime', {
                  required: t('happy-hour.create.start-time-required', 'Start time is required'),
                  validate: (value) => {
                    const endTime = watch('endTime');
                    if (value >= endTime) {
                      return t('happy-hour.create.start-time-before-end', 'Start time must be before end time');
                    }
                    return true;
                  },
                })}
              />
              {errors.startTime && (
                <Text color="red.500" fontSize="sm">
                  {errors.startTime.message}
                </Text>
              )}
            </Field.Root>

            <Field.Root>
              <Field.Label>{t('happy-hour.create.end-time', 'End Time')}</Field.Label>
              <Input
                type="time"
                borderColor={'gray.300'}
                rounded="sm"
                {...register('endTime', {
                  required: t('happy-hour.create.end-time-required', 'End time is required'),
                  validate: (value) => {
                    const startTime = watch('startTime');
                    if (value <= startTime) {
                      return t('happy-hour.create.end-time-after-start', 'End time must be after start time');
                    }
                    return true;
                  },
                })}
              />
              {errors.endTime && (
                <Text color="red.500" fontSize="sm">
                  {errors.endTime.message}
                </Text>
              )}
            </Field.Root>
          </>
        )}

        <Field.Root>
          <Field.Label>{t('happy-hour.create.days-of-week', 'Days of Week')}</Field.Label>
          <Controller
            control={control}
            name="daysOfWeek"
            rules={{
              required: t('happy-hour.create.days-of-week-required', 'Days of week is required'),
              validate: (value) =>
                value.length > 0 || t('happy-hour.create.days-of-week-required', 'Days of week is required'),
            }}
            render={({ field }) => (
              <SelectDaysOfWeek
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
              />
            )}
          />
          {errors.daysOfWeek && (
            <Text color="red.500" fontSize="sm">
              {errors.daysOfWeek.message}
            </Text>
          )}
        </Field.Root>
        {validationError.dayOfWeekConflicts && (
          <AlertError
            title={t('happy-hour.capacity-rule-conflict', 'Time rule conflict')}
            message={validationError.dayOfWeekConflicts}
          />
        )}
      </Fieldset.Content>
    </Fieldset.Root>
  );

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open);
      }}
      placement={'center'}
    >
      <Dialog.Trigger asChild>
        <Button variant={'surface'} p={2} bgColor={'white'}>
          {t('happy-hour.button.edit', 'Edit')}
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title fontSize={'xl'}>{t('happy-hour.dialog.update.title', 'Update Happy Hour')}</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>{FieldSet}</Dialog.Body>
            <Dialog.Footer>
              <HStack justifyContent={'space-between'} w={'full'}>
                <Button
                  loading={deleteLoading}
                  onClick={handleDelete}
                  p={2}
                  bg={'red.500'}
                  variant={'surface'}
                  color={'white'}
                >
                  {t('common.button.delete', 'Delete')}
                </Button>
                <HStack>
                  <Dialog.ActionTrigger asChild>
                    <Button variant="surface" p={2}>
                      {t('common.button.cancel', 'Cancel')}
                    </Button>
                  </Dialog.ActionTrigger>
                  <Button
                    loading={updateLoading}
                    onClick={() => {
                      void handleSubmit(onSubmit)();
                    }}
                    p={2}
                    bg={'gray.700'}
                    variant={'surface'}
                    color={'white'}
                    type="submit"
                  >
                    {t('common.button.update')}
                  </Button>
                </HStack>
              </HStack>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
